import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  ArrowUpRight,
  Award,
  BadgeCheck,
  Building2,
  Globe2,
  Handshake,
  HeartPulse,
  Lightbulb,
  Shield,
  Target,
  Users,
  Workflow,
} from "lucide-react";
import PulsingPillIcon from "./PulsingPillIcon";
import { primaryCtaClass } from "../utils/ctaClasses";
import { slowFadeUp, slowStagger } from "../utils/animations";
import { deploymentSiteCount, deploymentCountryCount } from "../data/impactMetrics";
import ganeshAnnaImage from "../assets/ganesh-anna.png";
import sangeetaGaneshImage from "../assets/sangeeta-ganesh.png";

function JourneyStepCard({ index, total, scrollYProgress, step }) {
  const StepIcon = step.Icon;
  const end = total > 1 ? index / (total - 1) : 1;
  const start = total > 1 ? (index - 1) / (total - 1) : 0;
  const isLast = index === total - 1;

  const opacity = useTransform(
    scrollYProgress,
    index === 0 ? [0, 1] : [start, end],
    index === 0 ? [1, 1] : [0, 1],
    { clamp: true }
  );
  const y = useTransform(
    scrollYProgress,
    index === 0 ? [0, 1] : [start, end],
    index === 0 ? [0, 0] : [16, 0],
    { clamp: true }
  );

  return (
    <motion.article
      style={{ opacity, y }}
      className={[
        "px-4 py-4 text-center",
        !isLast && "border-b border-slate-200 md:border-b-0",
        index > 0 && "md:border-l md:border-slate-200",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className={`mx-auto flex h-10 w-10 items-center justify-center rounded-xl ${step.iconTone}`}
      >
        <StepIcon className="h-5 w-5" strokeWidth={1.9} aria-hidden />
      </div>
      <p className="mt-3 text-sm font-bold text-black">{step.year}</p>
      <h4 className="mt-1 text-sm font-semibold text-secondary">{step.title}</h4>
      <p className="mt-2 text-xs leading-5 text-muted">{step.text}</p>
    </motion.article>
  );
}

export default function About() {
  const journeyScrollRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: journeyScrollRef,
    offset: ["start end", "center center"],
  });
  const founders = [
    {
      name: "Ganesh",
      role: "Founder & CEO",
      quote:
        "We envision a world where technology removes barriers in healthcare and empowers every provider to focus on what truly matters — patient care.",
      image: ganeshAnnaImage,
      imageAlt: "Ganesh, Founder and CEO of Gestalt",
    },
    {
      name: "Sangeeta Ganesh",
      role: "Co-Founder",
      quote:
        "Our work has always been rooted in partnership — building alongside hospital teams to deliver systems that are reliable, intuitive, and built for the realities of care.",
      image: sangeetaGaneshImage,
      imageAlt: "Sangeeta Ganesh, Co-Founder of Gestalt",
    },
  ];

  const pillars = [
    {
      badge: "Expertise",
      title: "18+ Years of Excellence",
      detail: "Deep domain expertise in healthcare technology.",
      accent: "bg-blue-50 text-blue-600",
      Icon: Award,
      iconTone: "bg-blue-100 text-blue-700",
    },
    {
      badge: "Healthcare",
      title: "Healthcare Specialists",
      detail: "A team of domain experts and innovators.",
      accent: "bg-emerald-50 text-emerald-600",
      Icon: HeartPulse,
      iconTone: "bg-emerald-100 text-emerald-700",
    },
    {
      badge: "Purpose-Driven",
      title: "Purpose-Driven",
      detail: "Our mission is to simplify healthcare and save lives.",
      accent: "bg-violet-50 text-violet-600",
      Icon: Target,
      iconTone: "bg-violet-100 text-violet-700",
    },
    {
      badge: "Customer",
      title: "Customer First",
      detail: "Long-term partnerships built on trust and success.",
      accent: "bg-amber-50 text-amber-600",
      Icon: Handshake,
      iconTone: "bg-amber-100 text-amber-800",
    },
  ];

  const values = [
    {
      title: "Integrity",
      text: "We do the right thing, always. Trust is the foundation of everything we build.",
      Icon: BadgeCheck,
      iconTone: "bg-sky-100 text-sky-700",
    },
    {
      title: "Innovation",
      text: "We embrace new ideas and technologies to solve real-world healthcare challenges.",
      Icon: Lightbulb,
      iconTone: "bg-violet-100 text-violet-700",
    },
    {
      title: "Security",
      text: "We prioritize data security and compliance to protect what matters most.",
      Icon: Shield,
      iconTone: "bg-emerald-100 text-emerald-700",
    },
    {
      title: "Care",
      text: "We build with empathy for patients and providers, driving better outcomes together.",
      Icon: HeartPulse,
      iconTone: "bg-rose-100 text-rose-700",
    },
    {
      title: "Excellence",
      text: "We are committed to quality, continuously improving in everything we do.",
      Icon: Award,
      iconTone: "bg-amber-100 text-amber-800",
    },
  ];

  const journey = [
    {
      year: "2008",
      title: "It Started with a Realization",
      text: "Our founders saw firsthand how disconnected systems were affecting healthcare delivery.",
      Icon: Building2,
      iconTone: "bg-blue-100 text-blue-700",
    },
    {
      year: "2010-2012",
      title: "First Steps",
      text: "We began working closely with hospitals to understand real workflows and challenges.",
      Icon: Users,
      iconTone: "bg-emerald-100 text-emerald-700",
    },
    {
      year: "2013-2016",
      title: "Building Solutions",
      text: "We developed our early systems, including GHealth, focusing on real-world impact.",
      Icon: Workflow,
      iconTone: "bg-violet-100 text-violet-700",
    },
    {
      year: "2017-2023",
      title: "Expanding Impact",
      text: `Our solutions were deployed across ${deploymentSiteCount}+ hospitals in ${deploymentCountryCount}+ countries, touching millions of lives.`,
      Icon: ArrowUpRight,
      iconTone: "bg-amber-100 text-amber-800",
    },
    {
      year: "2024 & Beyond",
      title: "Evolving to eSya",
      text: "Building a unified, intelligent platform for the future of healthcare.",
      Icon: Globe2,
      iconTone: "bg-sky-100 text-sky-800",
    },
  ];

  const deploymentLearnings = [
    {
      title: "No two hospitals operate the same way.",
      text: "Flexibility is essential.",
      Icon: Users,
      iconTone: "bg-blue-100 text-blue-700",
    },
    {
      title: "Integration is not optional, it is critical.",
      text: "Systems must work together seamlessly.",
      Icon: Workflow,
      iconTone: "bg-emerald-100 text-emerald-700",
    },
    {
      title: "User experience determines adoption.",
      text: "Simple, intuitive systems empower real users.",
      Icon: BadgeCheck,
      iconTone: "bg-violet-100 text-violet-700",
    },
    {
      title: "Data is powerful when it is connected.",
      text: "Visibility drives better decisions.",
      Icon: ArrowUpRight,
      iconTone: "bg-amber-100 text-amber-800",
    },
    {
      title: "Healthcare is local, but challenges are global.",
      text: "Systems must scale across regions and regulations.",
      Icon: Globe2,
      iconTone: "bg-sky-100 text-sky-800",
    },
  ];

  return (
    <section
      id="company"
      className="section-viewport scroll-mt-24 relative overflow-hidden bg-slate-50 px-4 py-12 md:px-6 md:py-20"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_45%_at_50%_-15%,rgba(20,184,166,0.12),transparent_55%)]"
        aria-hidden
      />
      <div className="relative mx-auto max-w-7xl">
        <div className="mx-auto mb-8 max-w-4xl text-center md:mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
            <PulsingPillIcon icon={Building2} tone="blue" />
            About Gestalt
          </span>
          <h2 className="text-section mt-5 font-bold leading-tight text-secondary md:mt-6">
            Your Trusted Partner in
            <br />
            <span className="bg-gradient-to-r from-accent to-blue-500 bg-clip-text text-transparent">
              Healthcare Innovation
            </span>
          </h2>
          <p className="mt-4 text-muted leading-7">
            For over 18 years, Gestalt has been committed to building reliable
            healthcare software that helps providers deliver better care and run
            more efficient operations.
          </p>
        </div>

        <motion.div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2 lg:gap-5">
          <motion.div
            variants={slowStagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="grid h-full grid-cols-1 gap-3 sm:grid-cols-2 sm:auto-rows-fr"
          >
              {pillars.map((pillar) => {
                const PillarIcon = pillar.Icon;
                return (
                <motion.article
                  variants={slowFadeUp}
                  key={pillar.title}
                  className="flex h-full min-h-[7.5rem] flex-row items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 transition hover:-translate-y-1 hover:shadow-md sm:min-h-0 sm:flex-col sm:items-stretch sm:gap-0 sm:p-4"
                >
                  <div
                    className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${pillar.iconTone}`}
                  >
                    <PillarIcon className="size-4" strokeWidth={1.75} aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span
                      className={`inline-flex w-fit rounded-md px-2 py-1 text-xs font-semibold ${pillar.accent}`}
                    >
                      {pillar.badge ?? pillar.title.split(" ")[0]}
                    </span>
                    <h3 className="mt-1.5 text-sm font-semibold leading-snug text-secondary sm:mt-2">
                      {pillar.title}
                    </h3>
                    <p className="mt-1 text-xs leading-5 text-muted sm:mt-auto sm:pt-1">
                      {pillar.detail}
                    </p>
                  </div>
                </motion.article>
              );
              })}
          </motion.div>

          <motion.div
            variants={slowStagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="grid h-full grid-cols-1 gap-3 sm:grid-cols-2 sm:auto-rows-fr lg:grid-cols-1"
          >
              {founders.map((founder) => (
                <motion.div
                  variants={slowFadeUp}
                  key={founder.name}
                  className="flex h-full min-h-[12rem] flex-col rounded-xl border border-slate-200 bg-white p-3 transition hover:-translate-y-1 hover:shadow-md sm:min-h-0 sm:p-4 lg:min-h-0"
                >
                  <div className="flex h-full flex-col items-center justify-center gap-3 text-center sm:gap-4">
                    {founder.image ? (
                      <img
                        src={founder.image}
                        alt={founder.imageAlt}
                        className="h-16 w-16 shrink-0 rounded-full object-cover object-top shadow-md ring-4 ring-slate-100 lg:h-[4.5rem] lg:w-[4.5rem]"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div
                        className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent/15 to-blue-500/20 text-base font-bold text-accent ring-4 ring-slate-100 lg:h-[4.5rem] lg:w-[4.5rem] lg:text-lg"
                        aria-hidden
                      >
                        {founder.initials}
                      </div>
                    )}
                    <div className="flex min-w-0 flex-1 flex-col justify-center">
                      <p className="text-xs leading-5 text-muted lg:text-[0.8125rem] lg:leading-6">
                        &ldquo;{founder.quote}&rdquo;
                      </p>
                      <p className="mt-2 text-sm font-semibold text-secondary lg:mt-3">
                        {founder.name}
                      </p>
                      <p className="text-xs text-muted">{founder.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
          </motion.div>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 gap-6">
          <div
            ref={journeyScrollRef}
            className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7"
          >
            <div className="text-center">
              <h3 className="font-display text-2xl font-bold text-secondary md:text-3xl">
                Our Journey
              </h3>
            </div>
            <div className="mt-7 grid grid-cols-1 gap-y-6 md:grid-cols-5 md:gap-y-0">
              {journey.map((step, index) => (
                <JourneyStepCard
                  key={step.year}
                  index={index}
                  total={journey.length}
                  scrollYProgress={scrollYProgress}
                  step={step}
                />
              ))}
            </div>
          </div>

          <motion.div
            variants={slowFadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
          >
          <div className="text-center">
            <h3 className="font-display text-2xl font-bold text-secondary md:text-3xl">
              What Real-World Deployments Taught Us
            </h3>
          </div>
          <div className="mt-7 grid grid-cols-1 gap-y-6 md:grid-cols-2 xl:grid-cols-5 xl:gap-y-0">
            {deploymentLearnings.map((item, index) => {
              const LearningIcon = item.Icon;
              const isLast = index === deploymentLearnings.length - 1;
              const isSecondCol = index % 2 === 1;

              return (
                <article
                  key={item.title}
                  className={[
                    "px-4 py-4 text-center",
                    !isLast && "border-b border-slate-200 xl:border-b-0",
                    isSecondCol && "md:border-l md:border-slate-200",
                    index > 0 && "xl:border-l xl:border-slate-200",
                    isLast &&
                      "md:col-span-2 md:max-w-md md:justify-self-center xl:col-span-1 xl:max-w-none",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div
                    className={`mx-auto flex h-10 w-10 items-center justify-center rounded-xl ${item.iconTone}`}
                  >
                    <LearningIcon className="h-5 w-5" strokeWidth={1.9} aria-hidden />
                  </div>
                  <h4 className="mt-3 text-sm font-semibold text-secondary">{item.title}</h4>
                  <p className="mt-2 text-xs leading-5 text-muted">{item.text}</p>
                </article>
              );
            })}
          </div>
          </motion.div>
        </div>

        <div className="mt-10 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
            <PulsingPillIcon icon={Award} tone="blue" />
            Our Values
          </span>
          <h3 className="font-display mt-4 text-2xl font-bold tracking-tight text-secondary md:text-3xl">
            Built on Values. Driven by Purpose.
          </h3>
        </div>

        <motion.div
          variants={slowStagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-5 grid grid-cols-1 gap-y-6 sm:grid-cols-2 xl:grid-cols-5 xl:gap-y-0"
        >
          {values.map((value, index) => {
            const ValueIcon = value.Icon;
            const isLast = index === values.length - 1;
            const isSecondCol = index % 2 === 1;

            return (
              <motion.article
                variants={slowFadeUp}
                key={value.title}
                className={[
                  "flex flex-row items-start gap-3 px-4 py-4 xl:flex-col xl:items-stretch xl:gap-0",
                  !isLast && "border-b border-slate-200 xl:border-b-0",
                  isSecondCol && "sm:border-l sm:border-slate-200",
                  index > 0 && "xl:border-l xl:border-slate-200",
                  isLast &&
                    "sm:col-span-2 sm:max-w-md sm:justify-self-center xl:col-span-1 xl:max-w-none",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div
                  className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${value.iconTone}`}
                >
                  <ValueIcon className="size-4" strokeWidth={1.75} aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-semibold text-secondary xl:mt-3">
                    {value.title}
                  </h4>
                  <p className="mt-1.5 text-xs text-muted leading-5 xl:mt-2">
                    {value.text}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </motion.div>

        <motion.div
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="relative mt-7 overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 p-5 md:p-7 text-white transition hover:-translate-y-1 hover:shadow-md"
        >
          <div
            className="absolute inset-0 bg-cover bg-[center_28%] opacity-40"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/images/cta-trusted-providers-bg.jpg)`,
            }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/90 to-blue-950/88"
            aria-hidden="true"
          />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.3fr_1.1fr_auto] gap-5 md:gap-6 items-center">
            <div>
              <p className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                More than a software provider...
              </p>
              <p className="mt-1 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-primary">
                We are your partner in transforming healthcare.
              </p>
            </div>
            <p className="text-base md:text-lg font-semibold text-blue-100 leading-snug">
              Let's build a healthier tomorrow - together.
            </p>
            <a
              href="#demo-form"
              className={`${primaryCtaClass} px-8 py-3.5 text-base font-bold`}
            >
              Connect with us
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
